/* globals PSPDFKit */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import FileProp from '../propTypes/fileProp';
import SkipStepButton from '../../sections/journey/overall/content/components/SkipStepButton';

const getBrowserFullscreenElementProp = () => {
  if (typeof document.fullscreenElement !== 'undefined') {
    return 'fullscreenElement';
  }
  if (typeof document.mozFullScreenElement !== 'undefined') {
    return 'mozFullScreenElement';
  }
  if (typeof document.msFullscreenElement !== 'undefined') {
    return 'msFullscreenElement';
  }
  if (typeof document.webkitFullscreenElement !== 'undefined') {
    return 'webkitFullscreenElement';
  }
  return null;
};

const PdfViewerComponent = forwardRef((props, ref) => {
  const {
    t,
    id,
    documentFile,
    pspdfkitLicenseKey,
    pdfToolbar,
    originalName,
    isFromCourse,
    onReadingFinished,
    onCompleteLesson,
    lessonIsDone,
    alreadyCompleted,
    onSkipStep,
    formFields,
    disabled,
    height,
  } = props;
  const containerRef = useRef(null);
  const instanceRef = useRef(null);
  let isFullScreen = false;

  const requestFullscreen = () => {
    const element = document.getElementById(id);
    const fullscreenMode = getBrowserFullscreenElementProp();
    switch (fullscreenMode) {
      case 'fullscreenElement':
        if (isFullScreen && document.fullscreenElement) {
          isFullScreen = false;
          document.exitFullscreen();
        } else {
          isFullScreen = true;
          element.requestFullscreen();
        }
        break;
      case 'mozFullScreenElement' /* Firefox */:
        if (isFullScreen && document.fullscreenElement) {
          isFullScreen = false;
          document.exitFullscreen();
        } else {
          isFullScreen = true;
          element.mozRequestFullScreen();
        }
        break;
      case 'msFullscreenElement' /* IE11 */:
        if (isFullScreen && document.fullscreenElement) {
          isFullScreen = false;
          document.exitFullscreen();
        } else {
          isFullScreen = true;
          element.msRequestFullscreen();
        }
        break;
      case 'webkitFullscreenElement' /* Safari has no support for document.fullscreenElement */:
        if (isFullScreen && document.webkitCurrentFullScreenElement) {
          isFullScreen = false;
          document.webkitExitFullscreen();
        } else {
          isFullScreen = true;
          element.webkitRequestFullscreen();
        }
        break;
      default:
        throw new Error('fullscreenElement is not supported by this browser');
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    const toolbarItemsToRemove = ['document-crop', 'document-editor'];
    let toolbar = pdfToolbar || [];

    // If there is not a toolbar item sent by properties use the defaultOne
    if (!toolbar || toolbar.length === 0) {
      // Get the default toolbar items.
      let { defaultToolbarItems } = PSPDFKit;
      defaultToolbarItems = defaultToolbarItems.filter(
        e => !toolbarItemsToRemove.includes(e.type),
      );
      toolbar = defaultToolbarItems;
    }

    const hasFullscreen = toolbar.find(
      type => type.id === 'pspdfkitFullscreen',
    );

    if (!hasFullscreen) {
      // Add a custom item to the list.
      toolbar.push({
        type: 'custom',
        id: 'pspdfkitFullscreen',
        title: 'Fullscreen',
        icon: '/images/documents/full-screen.svg',
        onPress: () => {
          requestFullscreen();
        },
      });
    }

    const pspdfkitConfig = {
      container,
      document: documentFile.url,
      toolbarItems: toolbar,
    };
    if (pspdfkitLicenseKey) {
      pspdfkitConfig.licenseKey = pspdfkitLicenseKey;
    }

    if (disabled) {
      pspdfkitConfig.isEditableAnnotation = function (ann) {
        return !(ann instanceof PSPDFKit.Annotations.WidgetAnnotation);
      };
    }

    // Fills the annotations and the form fields
    if (formFields) {
      pspdfkitConfig.instantJSON = formFields;
    }

    PSPDFKit.load(pspdfkitConfig)
      .then(instanceParam => {
        instanceRef.current = instanceParam;

        if (!formFields) {
          /* USE CASE FOR COURSE LESSON, ADDS AN EVENT LISTENER TO SCROLL TO FINISH CLASS WHEN PDF REACHES END */
          let currentPage = 0;
          const totalPages = instanceRef.current.totalPageCount;

          if (totalPages > 1) {
            // Page change
            instanceRef.current.addEventListener(
              'viewState.currentPageIndex.change',
              pageIndex => {
                currentPage = pageIndex + 1; // pageIndex is zero-based

                if (currentPage === totalPages) {
                  // User has read the full PDF.
                  onCompleteLesson();
                }
              },
            );
          } else {
            onCompleteLesson();
          }
        }
      })
      .catch(error => {
        console.error(error.message);
      });

    return () => {
      if (instanceRef.current) {
        // eslint-disable-next-line no-unused-expressions
        PSPDFKit.unload(instanceRef.current)
          ? (instanceRef.current = null)
          : console.log('error unloading pspdfkit at target', `#${id}`);
      }
    };
  }, [documentFile.url, onCompleteLesson]);

  useImperativeHandle(ref, () => ({
    exportPDF: async () => {
      if (instanceRef.current) {
        const arrayBuffer = await instanceRef.current.exportPDF({
          flatten: true,
        });
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const formData = new FormData();
        const file = new File([blob], originalName);

        formData.append('file', blob);
        return { formData, ext: '.pdf', size: file.size };
      }
      return null;
    },
    exportFormFields: async () => {
      try {
        if (instanceRef.current) {
          return instanceRef.current.exportInstantJSON();
        }
        return null;
      } catch (error) {
        console.error('Error exporting form fields:', error);
        return null;
      }
    },
    checkForSignatureField: async formFieldName => {
      try {
        if (instanceRef.current) {
          // First, get all `FormFields` in the `Document`.
          const fields = await instanceRef.current.getFormFields();

          // Get a signature form with the specific name you want.
          const field = fields.find(
            formField =>
              formField.name === formFieldName &&
              formField instanceof PSPDFKit.FormFields.SignatureFormField,
          );

          if (!field) {
            return null;
          }

          // Check overlapping ink annotations for the signature field.
          const overlappingAnnotations =
            await instanceRef.current.getOverlappingAnnotations(field);

          // Filter ink annotations from the overlapping annotations.
          const signatureAnnotations = overlappingAnnotations.filter(
            annotation =>
              annotation instanceof PSPDFKit.Annotations.InkAnnotation ||
              annotation instanceof PSPDFKit.Annotations.ImageAnnotation,
          );

          // Check if there is at least one valid annotation overlapping the signature field.
          return signatureAnnotations?.size > 0;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
  }));

  console.log(
    '*isFromCourse && !alreadyCompleted && lessonIsDone',
    isFromCourse,
    alreadyCompleted,
    lessonIsDone,
  );

  console.log('*Show', isFromCourse && !alreadyCompleted && lessonIsDone);

  return (
    <>
      <div className="pdfViewer-title">
        <h6 className="title">{t('preview.document')}</h6>
        {onSkipStep && (
          <div className="col-6 skip-wrapper">
            <SkipStepButton onClick={onSkipStep} />
          </div>
        )}
      </div>
      <div id={id} ref={containerRef} style={{ width: '100%', height }} />
      {isFromCourse && !alreadyCompleted && lessonIsDone && (
        <button
          type="button"
          onClick={onReadingFinished}
          className="btn m-btn--pill btn-dynamic"
        >
          {t('course.mark.asRead')}
        </button>
      )}
    </>
  );
});

PdfViewerComponent.propTypes = {
  t: PropTypes.func.isRequired,
  documentFile: PropTypes.shape(FileProp.propType).isRequired,
  id: PropTypes.string.isRequired,
  onReadingFinished: PropTypes.func,
  pspdfkitLicenseKey: PropTypes.string,
  originalName: PropTypes.string.isRequired,
  pdfToolbar: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    }),
  ),
  // This component is also used in Journeys
  isFromCourse: PropTypes.bool,
  onCompleteLesson: PropTypes.func,
  lessonIsDone: PropTypes.bool,
  alreadyCompleted: PropTypes.bool,
  onSkipStep: PropTypes.func,
  formFields: PropTypes.shape({}),
  disabled: PropTypes.bool,
  height: PropTypes.string,
};

PdfViewerComponent.defaultProps = {
  isFromCourse: false,
  alreadyCompleted: false,
  onReadingFinished: () => {},
  onCompleteLesson: () => {},
  lessonIsDone: false,
  pspdfkitLicenseKey: null,
  pdfToolbar: [],
  onSkipStep: null,
  formFields: null,
  disabled: false,
  height: '100vh',
};

export default PdfViewerComponent;
